import { markImpendingReload } from './reloadLoopMonitor'
import createLogger from './logger'
import { tryConsumeAutoReload } from './autoReloadBudget'

const recoveryLogger = createLogger('utils/recoveryUi')

/**
 * Unified “try one full reload this tab session, then tell the user” flow.
 * Each recovery reason uses its own sessionStorage key so paths do not block each other.
 */

export const RECOVERY_SESSION_KEYS = {
  /** Chunk load failed but deploy version matches — CDN/cache/offline blip. */
  chunkSameVersion: 'chunk-error-sv-reload-attempted',
  /** IndexedDB server process died (memory pressure, long backgrounding). */
  idbConnectionLost: 'idb-connection-lost-reload-attempted',
  /** IndexedDB object store missing (schema drift / corrupt cache, any Firebase or app DB). */
  idbMissingObjectStore: 'idb-missing-object-store-reload-attempted',
  /** @deprecated Use idbMissingObjectStore — kept so existing session flags still apply. */
  evidenceCacheSchema: 'evidence-cache-schema-reload-attempted',
} as const

/** Reload already triggered this document for a given recovery key (parallel rejections). */
const reloadInitiatedThisPage = new Set<string>()

/** Toast dedupe within the current document (parallel unhandledrejection handlers). */
const recoveryToastShownThisPage = new Set<string>()

/** Internal log dedupe (parallel unhandledrejection handlers). */
const recoveryLoggedThisPage = new Set<string>()

/**
 * Sets a session flag and reloads the page once per key. Safe if sessionStorage throws.
 * @returns true when recovery is in progress (reload started or already triggered this page);
 *   false when a reload was already attempted in a prior load — caller may show one toast.
 */
export function tryOneTimeSessionReload(sessionStorageKey: string): boolean {
  if (reloadInitiatedThisPage.has(sessionStorageKey)) {
    return true
  }

  try {
    if (sessionStorage.getItem(sessionStorageKey) === '1') {
      return false
    }

    const budget = tryConsumeAutoReload(`session_recovery:${sessionStorageKey}`)
    if (!budget.ok) {
      return false
    }

    reloadInitiatedThisPage.add(sessionStorageKey)
    sessionStorage.setItem(sessionStorageKey, '1')
    markImpendingReload(`session_recovery:${sessionStorageKey}`)
    window.location.reload()
    return true
  } catch {
    reloadInitiatedThisPage.delete(sessionStorageKey)
    return false
  }
}

export type GlobalToastKind = 'info' | 'warning' | 'error' | 'success'

/**
 * Uses global toast helpers from main.ts (after Pinia mount), or console fallback
 * when globals are not ready yet.
 */
export function notifyGlobalToast(kind: GlobalToastKind, message: string): void {
  const g = globalThis as {
    toastInfo?: (message: string) => void
    toastWarning?: (message: string) => void
    toastError?: (message: string) => void
    toastSuccess?: (message: string) => void
  }

  const fn =
    kind === 'info'
      ? g.toastInfo
      : kind === 'warning'
        ? g.toastWarning
        : kind === 'error'
          ? g.toastError
          : g.toastSuccess

  if (typeof fn === 'function') {
    fn(message)
    return
  }

  /* eslint-disable no-console -- fallback when toast globals are unavailable; match severity to DevTools */
  switch (kind) {
    case 'info':
      console.info(message)
      break
    case 'warning':
      console.warn(message)
      break
    case 'error':
      console.error(message)
      break
    case 'success':
      console.log(message)
      break
  }
  /* eslint-enable no-console */
}

/**
 * Shows a recovery toast at most once per page load for a given dedupe key.
 */
export function notifyGlobalToastOnce(
  dedupeKey: string,
  kind: GlobalToastKind,
  message: string,
): void {
  if (recoveryToastShownThisPage.has(dedupeKey)) {
    return
  }
  recoveryToastShownThisPage.add(dedupeKey)
  notifyGlobalToast(kind, message)
}

/**
 * Logs a recovery event at most once per page load. Use for transient IDB issues that
 * do not warrant a user-facing toast when the app continues to work (e.g. after reload).
 */
export function logRecoveryOnce(dedupeKey: string, message: string, reason?: unknown): void {
  if (recoveryLoggedThisPage.has(dedupeKey)) {
    return
  }
  recoveryLoggedThisPage.add(dedupeKey)
  recoveryLogger.warn(message, reason)
}
